import {
  useAssignReader,
  useGetMeterReaders,
} from "@features/mx/validations/hooks";
import { GenericTable } from "@shared/components/GenericTable";
import { Badge } from "@shared/ui/badge";
import { Button } from "@shared/ui/button";
import { PAGE_SIZE } from "@shared/utils/constants";
import { X } from "lucide-react";
import { useMemo, useState } from "react";

export const AssignReaderModal = ({ route, onClose }) => {
  const [selectedReaders, setSelectedReaders] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const { data: readersResponse } = useGetMeterReaders({
    page: currentPage,
    limit: PAGE_SIZE,
    search_data: searchTerm || undefined,
  });

  // Extract data from API response
  const readers = readersResponse?.results || [];

  const { mutateAsync: assignReader, isPending: isAssigning } =
    useAssignReader();

  const pagination = useMemo(() => {
    if (!readersResponse) {
      return {
        currentPage: 1,
        totalPages: 1,
        totalItems: 0,
        startItem: 0,
        endItem: 0,
        PAGE_SIZE,
      };
    }

    const totalItems = readersResponse.count;
    const totalPages = Math.ceil(totalItems / PAGE_SIZE);
    const startItem = totalItems === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
    const endItem = Math.min(currentPage * PAGE_SIZE, totalItems);

    return {
      currentPage,
      totalPages,
      totalItems,
      startItem,
      endItem,
      PAGE_SIZE,
    };
  }, [readersResponse, currentPage, PAGE_SIZE]);

  // Handle search from GenericTable
  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setCurrentPage(1); // Reset to first page when searching
  };

  // Handle page change
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  // Handle row selection from GenericTable
  const handleSelectionChange = (selectedIds) => {
    setSelectedReaders(selectedIds);
  };

  const handleAssign = async () => {
    await assignReader({
      id: route.id,
      readerId: selectedReaders,
    });
    onClose();
  };

  // Define columns for the GenericTable
  const columns = [
    {
      key: "employeeId",
      header: "Employee ID",
      sortable: false, // Disable client-side sorting since we're using API
      render: (value) => (
        <span className="font-medium text-gray-900">{value}</span>
      ),
    },
    {
      key: "name",
      header: "Name",
      sortable: false,
      render: (value) => <span className="text-gray-900">{value}</span>,
    },
    {
      key: "contactNumber",
      header: "Contact",
      render: (value, row) => (
        <div className="space-y-1">
          <div className="text-sm text-gray-900">{value}</div>
          <div className="text-xs text-gray-500">{row.contact_number}</div>
        </div>
      ),
    },
    {
      key: "city",
      header: "City",
      render: (value) => <span className="text-gray-700">{value}</span>,
    },
    {
      key: "assignedRoutes",
      header: "Workload",
      sortable: false,
      type: "badge",
      render: (value) => {
        const getWorkloadColor = (count) => {
          if (count === 0) return "bg-gray-100 text-gray-800";
          if (count <= 2) return "bg-green-100 text-green-800";
          if (count <= 5) return "bg-yellow-100 text-yellow-800";
          return "bg-red-100 text-red-800";
        };

        return (
          <Badge
            variant="secondary"
            className={`${getWorkloadColor(value)} font-medium`}
          >
            {value} routes
          </Badge>
        );
      },
    },
  ];

  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 flex justify-center items-center z-50">
      <div className="bg-white rounded-lg w-[70%] max-w-6xl max-h-[90vh] overflow-hidden shadow-lg">
        {/* Modal Header */}
        <div className="flex justify-between items-start p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              Assign Meter Reader to{" "}
              <span className="capitalize text-blue-600">{route?.name}</span>
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Select one or more readers to assign to this route.
            </p>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Table Content */}
        <div className="p-6 overflow-auto max-h-[calc(90vh-180px)]">
          <GenericTable
            data={readers}
            columns={columns}
            rowKey="id"
            selectable={true}
            selectedRows={selectedReaders}
            onSelectionChange={handleSelectionChange}
            onSearch={handleSearch}
            searchPlaceholder="Search by name, employee ID, or email..."
            searchDebounceMs={800}
            emptyMessage="No meter readers found matching your search criteria."
            pagination={pagination}
            onPageChange={handlePageChange}
          />
        </div>

        <div className="flex justify-end gap-3 p-6 border-t bg-gray-50">
          <Button variant="outline" onClick={onClose} disabled={isAssigning}>
            Cancel
          </Button>
          <Button
            onClick={handleAssign}
            disabled={selectedReaders.length === 0 || isAssigning}
            className="bg-blue-600 hover:bg-blue-700"
          >
            {isAssigning
              ? "Assigning..."
              : `Assign Selected (${selectedReaders.length})`}
          </Button>
        </div>
      </div>
    </div>
  );
};
